/**
 * Image types the API accepts (`api/src/routes/upload.ts` ALLOWED_MIMES). The
 * server also magic-number-sniffs the bytes, so whatever we declare in the data
 * URL has to be what the file really contains.
 */
const API_ALLOWED_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);

/**
 * Browsers can only ENCODE these. `canvas.toBlob` silently falls back to PNG for
 * anything else (notably iPhone `image/heic`), so we never ask it for a type it
 * cannot produce — we always encode JPEG and label the result JPEG.
 */
const CANVAS_OUTPUT_MIME = "image/jpeg";

/** Swaps a `.heic`/`.heif`/unknown extension for `.jpg` after conversion. */
function asJpegName(name: string): string {
  return /\.jpe?g$/i.test(name) ? name : `${name.replace(/\.[^.]+$/, "")}.jpg`;
}

/**
 * Resizes and compresses an image to be under the target size
 * @param file - The image file to resize
 * @param maxSizeMB - Maximum size in megabytes (default: 1MB)
 * @returns Promise<File> - The resized image file
 */
export async function resizeImage(
  file: File,
  maxSizeMB: number = 1
): Promise<File> {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // A format the API refuses (e.g. HEIC from an iPhone) must always go through
  // the canvas so it comes out as JPEG — even when it is already small enough.
  const needsConversion = !API_ALLOWED_MIMES.has(file.type);

  // If file is already under the target size, return it as is
  if (file.size <= maxSizeBytes && !needsConversion) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        // Start with a scale factor based on file size ratio
        const sizeRatio = Math.sqrt(maxSizeBytes / file.size);
        width = Math.floor(width * sizeRatio);
        height = Math.floor(height * sizeRatio);

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Function to compress with quality adjustment
        const compressImage = (quality: number): Promise<Blob | null> => {
          return new Promise((resolveBlob) => {
            canvas.toBlob(
              (blob) => {
                resolveBlob(blob);
              },
              CANVAS_OUTPUT_MIME,
              quality
            );
          });
        };

        // Binary search for optimal quality
        const findOptimalQuality = async (): Promise<Blob | null> => {
          let minQuality = 0.1;
          let maxQuality = 0.95;
          let optimalBlob: Blob | null = null;

          // Try different quality settings
          for (let i = 0; i < 10; i++) {
            const quality = (minQuality + maxQuality) / 2;
            const blob = await compressImage(quality);

            if (!blob) continue;

            if (blob.size <= maxSizeBytes) {
              optimalBlob = blob;
              minQuality = quality; // Try higher quality
            } else {
              maxQuality = quality; // Need lower quality
            }

            // If we're close enough, use this blob
            if (
              optimalBlob &&
              blob.size <= maxSizeBytes &&
              blob.size > maxSizeBytes * 0.8
            ) {
              break;
            }
          }

          // If still too large, reduce dimensions
          if (!optimalBlob || optimalBlob.size > maxSizeBytes) {
            width = Math.floor(width * 0.8);
            height = Math.floor(height * 0.8);
            
            if (width < 100 || height < 100) {
              reject(new Error("Unable to compress image to target size"));
              return null;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, width, height);
            
            return findOptimalQuality();
          }

          return optimalBlob;
        };

        findOptimalQuality()
          .then((blob: Blob | null) => {
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }
            // Label the file with what the canvas actually produced, not with
            // the source type — they differ for every non-JPEG input.
            const resizedFile = new File([blob], asJpegName(file.name), {
              type: CANVAS_OUTPUT_MIME,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          })
          .catch(reject);
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
    };
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
  });
}

export async function convertToBase64(file: File): Promise<string> {
  const base64String = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });

  // Return the data URL exactly as FileReader produced it. It previously
  // rewrote every prefix to `data:image/png;base64,` regardless of the real
  // content, so the API's magic-number check rejected every non-PNG upload
  // (camera photos are JPEG) with 400 while screenshots happened to pass.
  return base64String;
}
